import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPayersByAccountQuery } from "store/queries/payers";

import { specialtyDropdown } from "../../../helpers/providerConstants";
import { formValidationConstants } from "../helpers/formValidations";

export function ClearFee() {
  const [expanded, setExpanded] = useState(true);
  const {
    control,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<any>();

  const { id: accountId } = useParams<{ id: string }>();

  // We only fetch payers if we have an accountId which means we are in edit page, not int he create page
  const { data: payerByAccount, isLoading: loadingPayersName } =
    useGetPayersByAccountQuery(accountId || "", {
      skip: !accountId,
    });

  // Generate payer options from the fetched data
  const payerNameOptions = sortBy(
    payerByAccount?.data?.map((item: any) => ({
      name: item.name,
      value: { name: item.name, payerId: item.id },
    })) || [],
    ["name"]
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payerAccountFees",
  });

  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();

  // Only show Clear Patient Fee section if we have an accountId and payers are available
  const showClearPatientFee =
    accountId && payerNameOptions.length > 0 && !loadingPayersName;

  // We need to watch all payer selections to check for duplicates
  const watchedPayerFees = watch("payerAccountFees") || [];

  const addPayerFee = () => {
    append({
      payerId: "",
      payerName: "",
      type: "ClearPatientFee",
      valueInPercentage: 0,
    });
  };

  // Function to check if a payer is already selected
  const isPayerAlreadySelected = (currentIndex: number, payerId: string) => {
    if (!payerId) return false;
    return watchedPayerFees.some(
      (fee: any, index: number) =>
        index !== currentIndex && fee.payerId === payerId
    );
  };

  return (
    <div className="block bg-white border-round-xl p-4 border-1 border-gray-300">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">
          Clear Fee Section
        </span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4">
          <div className="formgrid grid mt-4 border-bottom-1 pb-2 border-gray-300">
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearFeeForClearPackage">
                Fee for Clear package
              </Label>
              <Controller
                name="clearFeeForClearPackage"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_FEE_FOR_CLEAR_PACKAGE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearFeeForClearPackage"
                      autoComplete="off"
                      type="number"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearFeeForClearPackage"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearFeeForClearPackage",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearFeeForClearPackage", errors)}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearTransactionFeeForPR">
                Fee for Patient Responsibility
              </Label>
              <Controller
                name="clearTransactionFeeForPR"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_TRANSACTION_FEE_FOR_PR,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearTransactionFeeForPR"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearTransactionFeeForPR"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearTransactionFeeForPR",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearTransactionFeeForPR", errors)}
                  </>
                )}
              />
            </div>
            {/* <div className="field col-12 md:col-4">
              <Label htmlFor="clearFinancingFee">Financing Fee</Label>
              <Controller
                name="clearFinancingFee"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE.CLEAR_FINANCING_FEE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearFinancingFee"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearFinancingFee"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearFinancingFee",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearFinancingFee", errors)}
                  </>
                )}
              />
            </div> */}
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearHealthDiscountForGFE">GFE Discount</Label>
              <Controller
                name="clearHealthDiscountForGFE"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .CLEAR_HEALTH_DISCOUNT_FOR_GFE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearHealthDiscountForGFE"
                      autoComplete="off"
                      value={value ?? ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearHealthDiscountForGFE"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearHealthDiscountForGFE",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearHealthDiscountForGFE", errors)}
                  </>
                )}
              />
            </div>
            {/* <div className="field col-12 md:col-4">
              <Label htmlFor="prFeeMethod">
                Patient Responsibility Fee method
              </Label>
              <Controller
                data-testid="prFeeMethod"
                name="prFeeMethod"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="prFeeMethod"
                    data-testid="prFeeMethod__Dropdown"
                    value={value}
                    onChange={onChange}
                    options={Object.values(specialtyDropdown)}
                    optionLabel="name"
                    placeholder="Select"
                    className="w-full"
                    disabled
                  />
                )}
              />
            </div> */}
            <div className="field col-12 md:col-4">
              <Label htmlFor="patientResponsibilityDiscount">
                Patient Responsibility discount
              </Label>
              <Controller
                name="patientResponsibilityDiscount"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE
                      .PATIENT_RESPONSIBILITY_DISCOUNT,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="patientResponsibilityDiscount"
                      autoComplete="off"
                      value={value ?? ""}
                      placeholder="Enter amount here"
                      aria-labelledby="patientResponsibilityDiscount"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "patientResponsibilityDiscount",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage(
                      "patientResponsibilityDiscount",
                      errors
                    )}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="clearHealthFeeForGFE">Fee for GFE</Label>
              <Controller
                name="clearHealthFeeForGFE"
                control={control}
                rules={{
                  required:
                    formValidationConstants.CLEAR_FEE.CLEAR_HEALTH_FEE_FOR_GFE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      className="w-full"
                      id="clearHealthFeeForGFE"
                      autoComplete="off"
                      value={value || ""}
                      placeholder="Enter amount here"
                      aria-labelledby="clearHealthFeeForGFE"
                      type="number"
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "clearHealthFeeForGFE",
                          trigger,
                        });
                      }}
                      onChange={onChange}
                    />
                    {getFormErrorMessage("clearHealthFeeForGFE", errors)}
                  </>
                )}
              />
            </div>
            <div className="field col-12 md:col-4">
              <Label htmlFor="expirationDays">Expiration Number of Days</Label>
              <Controller
                name="expirationDays"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputText
                    className="w-full"
                    id="expirationDays"
                    autoComplete="off"
                    value={value}
                    placeholder="Enter expiration number of days"
                    aria-labelledby="expirationDays"
                    type="number"
                    min={0}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "expirationDays",
                        trigger,
                      });
                    }}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
          {showClearPatientFee && (
            <div className="block bg-white border-round">
              <div className="flex justify-content-between mb-3">
                <span className="text-black font-semibold text-md m-0">
                  Clear Patient Fee
                </span>
                <Button
                  type="button"
                  label="Add Payer Fee"
                  icon="pi pi-plus"
                  onClick={addPayerFee}
                  className="p-button-outlined"
                />
              </div>

              {fields.length === 0 && (
                <p className="text-gray-600 mb-3">No payer fees configured.</p>
              )}

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="formgrid grid mb-3 p-3 border-1 border-round surface-border"
                >
                  <div className="field col-5">
                    <Label htmlFor={`payerAccountFees.${index}.payerId`}>
                      Payer
                    </Label>
                    <Controller
                      name={`payerAccountFees.${index}.payerId`}
                      control={control}
                      rules={{
                        required: "Payer is required",
                        validate: (value) => {
                          if (isPayerAlreadySelected(index, value)) {
                            return "This payer has already been selected";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => {
                        const isDuplicate = isPayerAlreadySelected(
                          index,
                          value
                        );
                        return (
                          <>
                            <Dropdown
                              id={`payerAccountFees.${index}.payerId`}
                              value={value}
                              onChange={(e) => {
                                onChange(e.value);
                                // Trigger validation for all payer fields to update duplicate errors
                                setTimeout(() => {
                                  watchedPayerFees.forEach(
                                    (_: any, idx: number) => {
                                      trigger(
                                        `payerAccountFees.${idx}.payerId`
                                      );
                                    }
                                  );
                                }, 0);
                              }}
                              options={payerNameOptions.map((payer) => ({
                                label: payer.value.name,
                                value: payer.value.payerId,
                              }))}
                              placeholder="Select Payer"
                              className={`w-full ${
                                isDuplicate ? "p-invalid" : ""
                              }`}
                            />
                            {getFormErrorMessage(
                              `payerAccountFees.${index}.payerId`,
                              errors
                            )}
                          </>
                        );
                      }}
                    />
                  </div>

                  <div className="field col-5">
                    <Label
                      htmlFor={`payerAccountFees.${index}.valueInPercentage`}
                    >
                      Clear Patient Fee
                    </Label>
                    <Controller
                      name={`payerAccountFees.${index}.valueInPercentage`}
                      control={control}
                      rules={{
                        required:
                          formValidationConstants.CLEAR_FEE.CLEAR_PATIENT_FEE,
                        min: {
                          value: 0.01,
                          message: "Value must be greater than 0",
                        },
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <>
                          <InputText
                            className="w-full"
                            id={`payerAccountFees.${index}.valueInPercentage`}
                            autoComplete="off"
                            type="number"
                            value={value || ""}
                            placeholder="Enter fee amount"
                            min={0}
                            onBlur={() => {
                              handleOnBlurField({
                                onBlur,
                                field: `payerAccountFees.${index}.valueInPercentage`,
                                trigger,
                              });
                            }}
                            onChange={onChange}
                          />
                          {getFormErrorMessage(
                            `payerAccountFees.${index}.valueInPercentage`,
                            errors
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div className="field col-2 flex align-items-end">
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-danger p-button-outlined w-full"
                      onClick={() => remove(index)}
                      tooltip="Remove payer fee"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
